import { Injectable, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { NotificationService } from './notification.service';
import { UserService } from './user.services';

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  private isOnlineSubject: BehaviorSubject<boolean>;
  public isOnline$: Observable<boolean>;

  private lastOfflineNotificationTime = 0;
  private readonly NOTIFICATION_DEBOUNCE_MS = 3000;
  private wasPreviouslyOffline = false;

  constructor(
    private notificationService: NotificationService,
    private userService: UserService,
    private ngZone: NgZone
  ) {
    const initialStatus = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.isOnlineSubject = new BehaviorSubject<boolean>(initialStatus);
    this.isOnline$ = this.isOnlineSubject.asObservable();

    this.initConnectionListeners();
  }

  private initConnectionListeners(): void {
    if (typeof window === 'undefined') return;

    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    merge(online$, offline$).subscribe((isOnline) => {
      this.ngZone.run(() => {
        this.isOnlineSubject.next(isOnline);

        if (!isOnline) {
          this.wasPreviouslyOffline = true;
          this.notifyNoInternet();
        } else if (this.wasPreviouslyOffline) {
          this.wasPreviouslyOffline = false;
          this.notifyConnectionRestored();
        }
      });
    });
  }

  public get isOnline(): boolean {
    return this.isOnlineSubject.value;
  }

  public get isOffline(): boolean {
    return !this.isOnline;
  }

  /**
   * Determines whether an HTTP error is due to missing internet connection or network failure.
   */
  public isNetworkError(error: any): boolean {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return true;
    }

    if (typeof error?.message === 'string') {
      const msg = error.message.toLowerCase();
      if (
        msg.includes('net::err_internet_disconnected') ||
        msg.includes('net::err_name_not_resolved')
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Returns a localized error message explaining the lack of internet connection.
   */
  public getNoInternetMessage(): string {
    const lang = this.getCurrentLanguage();
    if (lang.toLowerCase().startsWith('ar')) {
      return 'لا يوجد اتصال بالإنترنت. يرجى التحقق من اتصالك بالشبكة والمحاولة مرة أخرى.';
    }
    return 'No internet connection. Please check your network connection and try again.';
  }

  /**
   * Returns a localized message indicating connection restoration.
   */
  public getConnectionRestoredMessage(): string {
    const lang = this.getCurrentLanguage();
    if (lang.toLowerCase().startsWith('ar')) {
      return 'تمت استعادة الاتصال بالإنترنت بنجاح.';
    }
    return 'Internet connection restored.';
  }

  /**
   * Triggers the no-internet error notification, throttled to prevent spam from parallel requests.
   */
  public notifyNoInternet(): void {
    const now = Date.now();
    if (now - this.lastOfflineNotificationTime > this.NOTIFICATION_DEBOUNCE_MS) {
      this.lastOfflineNotificationTime = now;
      this.notificationService.error(this.getNoInternetMessage());
    }
  }

  /**
   * Triggers the connection-restored success notification.
   */
  public notifyConnectionRestored(): void {
    this.notificationService.success(this.getConnectionRestoredMessage());
  }

  private getCurrentLanguage(): string {
    return (
      localStorage.getItem('language') ||
      this.userService?.getCurrentSystemLanguage() ||
      'en-US'
    );
  }
}
